import { SourceConfig, CandidatePayload } from '../types';
import {
  compressAvitoLocationIds,
  fetchAvitoLocations,
  fetchAvitoNationalities,
  fetchAvitoResumeSpecializations,
} from '../../services/avitoDictionariesService';

/**
 * MVP-конфиг Avito.
 *
 * Сейчас включены только две секции — `specialization` и `metro` — для
 * проверки что архитектура с `dataLoaders` / `uiHints` работает: модалка
 * специализаций отрисуется в "пустом" состоянии (dataLoader не подключён),
 * локация — тоже без словаря.
 *
 * После подключения соответствующих fetch-функций (avitoDictionariesService
 * на фронте) сюда добавятся:
 *   dataLoaders: {
 *     specialization: fetchAvitoResumeSpecializations, // плоский CSV-справочник
 *     locations:      fetchAvitoLocations,      // иерархия из XML
 *     nationality:    fetchAvitoNationalities,  // плоский XML
 *   }
 *
 * toPayload передаёт avitoChatId, который появляется только после платного
 * открытия контактов через /avito/resume/:id/open-contacts.
 */
export const avitoConfig: SourceConfig = {
  id: 'avito',
  label: 'Авито',
  aiRecruiterSource: 'AVITO',
  enabledMessengers: ['AVITO'],
  messengerPriority: ['AVITO', 'MAX', 'TELEGRAM', 'WHATSAPP', 'HH'],
  // Словарные секции (пользуются dataLoaders ниже): specialization, metro,
  // citizenship, география. Avito-only enum/числовые/датовые секции рендерятся
  // прямо в Filters.tsx чекбоксами/инпутами, без отдельных модалок —
  // см. блоки `hasSection('avitoXxx')`.
  filterSections: [
    'specialization',
    // 'metro' — НЕ добавляем: hasSection('metro') в Filters.tsx рендерит
    // HH-механику со списком линий и станций (SearchableMetroSelect),
    // для Avito это сломанный UI. Когда появится отдельный AvitoMetroPopup —
    // вернём как новую секцию.
    'citizenship',
    'avitoEducationLevel',
    'avitoSchedule',
    'avitoBusinessTrip',
    'avitoRelocation',
    'avitoKeywords',
    'avitoExcludedWords',
    'avitoExperienceRange',
    'avitoDriverLicence',
    'avitoDriverLicenceCategory',
    'avitoDrivingExperience',
    'avitoOwnTransport',
    'avitoMedicalBook',
    'avitoUpdatedDate',
    'age'
  ],
  dataLoaders: {
    // Avito specialization для поиска резюме — плоский CSV-справочник через
    // GET /api/avito/dictionaries/resume_specializations.
    specialization: fetchAvitoResumeSpecializations,
    // Avito locations — иерархия регион→город→метро из публичного
    // Locations.xml на CDN Авито (распарсенного на бэке). Структура
    // приведена к SpecializationOption ({id, name, roles?}) чтобы те же
    // универсальные компоненты могли её рендерить как и hh.
    locations: fetchAvitoLocations,
    // Avito гражданство — плоский XML catalog_nationality.xml.
    nationality: fetchAvitoNationalities,
  },
  uiHints: {
    specializationMode: 'flat',
    locationMode: 'hierarchical',
  },
  postProcessors: {
    compressCityIds: compressAvitoLocationIds,
    getAreaNameById: getAvitoLocationNameById,
  },

  toPayload(candidate: Record<string, unknown>): CandidatePayload {
    const fullName = normalizeString(candidate.fullName)
      || [candidate.lastName, candidate.firstName, candidate.middleName]
        .map(normalizeString)
        .filter(Boolean)
        .join(' ')
      || normalizeString(candidate.name)
      || normalizeString(candidate.title)
      || '';
    const resumeUrl = normalizeString(candidate.resumeUrl) || normalizeString(candidate.url);

    return {
      externalId: String(candidate.id ?? ''),
      source: 'AVITO',
      sourceExternalId: String(candidate.id ?? ''),
      email: normalizeString(candidate.email),
      messengerContacts: {
        telegram: normalizeString(candidate.telegram),
        whatsapp: normalizeString(candidate.whatsapp),
        phone: normalizeString(candidate.phone),
        email: normalizeString(candidate.email),
        max: null,
        avitoChatId: normalizeString(candidate.avitoChatId),
        hhResumeId: null,
      },
      name: fullName,
      firstName: normalizeString(candidate.firstName),
      lastName: normalizeString(candidate.lastName),
      middleName: normalizeString(candidate.middleName),
      photoUrl: normalizeString(candidate.photoUrl) || normalizeString(candidate.photo),
      resumeUrl,
      resumeBrief: normalizeString(candidate.resumeBrief) || normalizeString(candidate.title) || '',
      metadata: {
        source: 'avito',
        resumeUrl,
        raw: candidate,
      },
    };
  },
};

let avitoLocationNamesCache: Map<string, string> | null = null;
let avitoLocationNamesCachePromise: Promise<Map<string, string>> | null = null;

async function getAvitoLocationNameById(id: string): Promise<string> {
  const normalizedId = String(id).split('/').pop() || String(id);

  if (!avitoLocationNamesCache) {
    avitoLocationNamesCachePromise = avitoLocationNamesCachePromise || fetchAvitoLocations().then((items) => {
      const names = new Map<string, string>();
      const collect = (nodes: Array<{ id: string | number; name: string; areas?: any[] }>) => {
        nodes.forEach((node) => {
          names.set(String(node.id), node.name);
          if (node.areas && node.areas.length > 0) {
            collect(node.areas);
          }
        });
      };
      collect(items);
      return names;
    });
    try {
      avitoLocationNamesCache = await avitoLocationNamesCachePromise;
    } catch (error) {
      avitoLocationNamesCachePromise = null;
      throw error;
    }
  }

  return avitoLocationNamesCache.get(normalizedId) || String(id);
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

export default avitoConfig;
